import React, { useState } from "react";

const LEVEL_NAMES = {
  1: "Find Me",
  2: "Odd One Out",
  3: "Memory",
  4: "Match",
  5: "Think5Whys",
};

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * Renders from the `history` array already fetched ONCE by useGame
 * (point 6) — this component never triggers its own API calls.
 * Each daily-play doc holds levelScores[0..4]; we flatten to rows,
 * already naturally best-score-only since the DB only stores the
 * day's actual completed scores (point 7's "auto-organised" outcome).
 */
export default function ScoreHistoryPanel({ history = [] }) {
  const [expanded, setExpanded] = useState(false);

  // History is already sorted desc by playDate from the API (point 8),
  // flatten each day's 5 level scores into individual rows.
  const rows = history
    .filter((day) => day.completed)
    .flatMap((day) =>
      day.levelScores.map((score, idx) => ({
        date: day.playDate,
        level: idx + 1,
        setName: day.theme,
        points: score,
        timeTakenSec: day.totalTimeSec ? Math.round(day.totalTimeSec / 5) : null,
      }))
    )
    .filter((r) => r.points > 0);

  const visibleRows = expanded ? rows : rows.slice(0, 6);

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#F3EEFF] p-4 text-center text-sm text-[#8B7FA8]">
        No play history yet — your first completed day will show up here! 🌱
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#F3EEFF] overflow-hidden">
      <div className="px-4 pt-3 pb-2">
        <h3 className="text-sm font-bold text-[#2B2140]">Score History</h3>
      </div>

      {/* Mobile: stacked cards. Desktop/tablet: table. */}
      <div className="sm:hidden divide-y divide-[#F3EEFF]">
        {visibleRows.map((r, i) => (
          <div key={i} className="px-4 py-2.5 flex items-center justify-between text-xs">
            <div>
              <p className="font-semibold text-[#2B2140]">{formatDate(r.date)}</p>
              <p className="text-[#8B7FA8]">
                L{r.level} · {LEVEL_NAMES[r.level]} · {r.setName}
              </p>
            </div>
            <div className="text-right">
              <p className="font-bold text-[#7C3AED]">{r.points} pts</p>
              {r.timeTakenSec != null && <p className="text-[#8B7FA8]">{formatTime(r.timeTakenSec)}</p>}
            </div>
          </div>
        ))}
      </div>

      <table className="hidden sm:table w-full text-sm">
        <thead>
          <tr className="text-left text-[#8B7FA8] text-xs uppercase border-b border-[#F3EEFF]">
            <th className="px-4 py-2 font-semibold">Date</th>
            <th className="px-4 py-2 font-semibold">Level</th>
            <th className="px-4 py-2 font-semibold">Set</th>
            <th className="px-4 py-2 font-semibold">Points</th>
            <th className="px-4 py-2 font-semibold">Time Taken</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => (
            <tr key={i} className="border-b border-[#F9F7FF] last:border-0">
              <td className="px-4 py-2 text-[#2B2140]">{formatDate(r.date)}</td>
              <td className="px-4 py-2 text-[#2B2140]">L{r.level} · {LEVEL_NAMES[r.level]}</td>
              <td className="px-4 py-2 text-[#8B7FA8]">{r.setName}</td>
              <td className="px-4 py-2 font-bold text-[#7C3AED]">{r.points}</td>
              <td className="px-4 py-2 text-[#8B7FA8]">{r.timeTakenSec != null ? formatTime(r.timeTakenSec) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length > 6 && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="w-full text-xs font-semibold text-[#7C3AED] py-2.5 hover:bg-[#F3EEFF] transition-colors"
        >
          {expanded ? "Show less" : `Show all (${rows.length})`}
        </button>
      )}
    </div>
  );
}
